import { useState } from 'react';
import Modal from '../../../../../../shared/frontend/components/modal/modal';
import { Button } from '../../../../../../shared/frontend/components/presentational/buttons/Button';
import { useFeature } from '../../../../../../shared/frontend/scripts/selectors/global';
import { FeatureFlag } from '../../../../../../shared/gameConfig/sharedTypes';
import { TCombatLogEntityType } from '../../../../shared/types/combatLogDownloadTypes';
import CombatLogSegmentsTable from './CombatLogSegmentsTable';
import useCombatLogSegments from './useCombatLogSegments';

type TCombatLogDownloadModalProps = {
  type: TCombatLogEntityType;
  correlationId: string;
  subtitle: string;
  isOpen: boolean;
  onClose: () => void;
};

function CombatLogDownloadModal({
  type,
  correlationId,
  subtitle,
  isOpen,
  onClose
}: TCombatLogDownloadModalProps) {
  const {
    data,
    sources,
    rows,
    normalSegments,
    loadError,
    showLoadingState,
    downloadingKeys,
    isDownloadingAll,
    loadSegments,
    downloadSegment,
    downloadAllSegments
  } = useCombatLogSegments({ isOpen, type, correlationId });

  return (
    <Modal
      centered
      className="rio-combatlog-download-modal"
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      title="Live Tracking Debug Panel"
    >
      <Modal.Body className="rio-combatlog-download-modal__body">
        <div
          className="slds-text-title--caps slds-text-heading--small slds-m-bottom--x-small"
          style={{ color: '#fff', fontWeight: 700 }}
        >
          Download log segments
        </div>
        <div className="slds-grid slds-grid--align-spread slds-grid--vertical-align-center slds-m-bottom--small">
          <div className="slds-text-body--small" style={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
            {subtitle}
          </div>
          {normalSegments.length > 0 && (
            <Button variant="outlined" onClick={() => void downloadAllSegments()} disabled={isDownloadingAll}>
              {isDownloadingAll ? 'Downloading...' : `Download All (${normalSegments.length})`}
            </Button>
          )}
        </div>

        {showLoadingState && (
          <div
            className="slds-p-around--large slds-text-align--center slds-is-relative"
            style={{ minHeight: 160 }}
          >
            <div
              style={{
                minHeight: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 12,
                color: '#fff'
              }}
            >
              <i className="fa fa-spinner fa-spin" style={{ fontSize: '2rem' }} />
              <div className="slds-text-body--small">Loading log segments...</div>
            </div>
          </div>
        )}

        {!showLoadingState && loadError && (
          <div className="slds-p-around--large slds-text-align--center">
            <div className="slds-m-bottom--small">{loadError}</div>
            <Button onClick={() => void loadSegments()}>Retry</Button>
          </div>
        )}

        {!showLoadingState && !loadError && data && sources.length === 0 && (
          <div className="slds-p-around--large slds-text-align--center">
            No combat log segments found for this encounter/run.
          </div>
        )}

        {!showLoadingState && !loadError && rows.length > 0 && (
          <CombatLogSegmentsTable rows={rows} downloadingKeys={downloadingKeys} onDownload={downloadSegment} />
        )}
      </Modal.Body>

    </Modal>
  );
}

export default function CombatLogDownloadIcon(props: {
  type: TCombatLogEntityType;
  correlationId?: string | null;
  subtitle: string;
}) {
  const canDownloadCombatLogs = useFeature(FeatureFlag.COMBAT_LOG_DOWNLOAD);
  const [isOpen, setIsOpen] = useState(false);

  if (!canDownloadCombatLogs || !props.correlationId) {
    return null;
  }

  return (
    <>
      <button
        className="button-link"
        type="button"
        aria-label="Download combat log segments"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(true);
        }}
        style={{
          background: 'none',
          border: 0,
          color: '#fff',
          marginTop: 4,
          cursor: 'pointer',
          outline: 'none',
          boxShadow: 'none',
          padding: 0,
          lineHeight: 1
        }}
      >
        <i className="fa fa-bug" />
      </button>

      <CombatLogDownloadModal
        type={props.type}
        correlationId={props.correlationId}
        subtitle={props.subtitle}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}
